// =============================================================================
// undoTipWarning — what Undo tip is about to orphan.
//
// undo_tipping deletes the tip (the lot). Everything packed under that lot —
// pallet lines (source_tipping_id) and rejects (reject_log.tipping_id) — is
// kept but falls to Unassigned on the run (ON DELETE SET NULL, mig
// 20261019100000): it stops counting against that tip's received pallet,
// delivery or batch in the Lots view and the Packout report. The undo itself
// is still allowed; the confirm just says so first.
//
// A failed read is said as such — never as "nothing was packed".
// =============================================================================

export interface TipPackout {
  pallets:  number
  cartons:  number
  rejectKg: number
}

type LineRow   = { pallet_id: string; carton_count: number | null; pallets: { deleted_at: string | null } | { deleted_at: string | null }[] | null }
type RejectRow = { weight_kg: number | null }

const palletLive = (r: LineRow) => {
  const p = Array.isArray(r.pallets) ? r.pallets[0] : r.pallets
  return !p?.deleted_at
}

/** Live pallet lines and rejects under the tip → counts. */
export function summariseTipPackout(lines: LineRow[], rejects: RejectRow[]): TipPackout {
  const live = lines.filter(palletLive)
  return {
    pallets:  new Set(live.map(l => l.pallet_id)).size,
    cartons:  live.reduce((s, l) => s + (l.carton_count ?? 0), 0),
    rejectKg: rejects.reduce((s, r) => s + Number(r.weight_kg ?? 0), 0),
  }
}

/** The amber lines above Undo tip's confirm; none when nothing hangs off the tip. */
export function undoTipWarnings(p: TipPackout | null): string[] {
  if (p === null) {
    return ["Couldn't check what was packed from this tip. Anything packed from it will show as Unassigned on the run after the undo."]
  }
  const parts: string[] = []
  if (p.pallets > 0) parts.push(`${p.pallets} pallet${p.pallets === 1 ? '' : 's'} (${p.cartons} ctns)`)
  if (p.rejectKg > 0) parts.push(`${p.rejectKg.toFixed(1)} kg of rejects`)
  if (parts.length === 0) return []
  return [`Already packed from this tip: ${parts.join(' and ')}. After the undo they show as Unassigned on the run, and no longer count against where this tip came from. Pick their Lot again afterwards.`]
}

interface ReadClient {
  from: (table: string) => any  // untyped client (repo standard for long selects)
}

/** Reads what hangs off a tip; null when either read fails. */
export async function readTipPackout(db: ReadClient, tippingId: string): Promise<TipPackout | null> {
  try {
    const [lines, rejects] = await Promise.all([
      // One tip's lines and rejects: tens, never near a page — bounded anyway.
      db.from('pallet_lines').select('pallet_id, carton_count, pallets(deleted_at)')
        .eq('source_tipping_id', tippingId).is('deleted_at', null).range(0, 999),
      db.from('reject_log').select('weight_kg').eq('tipping_id', tippingId).is('deleted_at', null).range(0, 999),
    ])
    if (lines.error || rejects.error) return null
    return summariseTipPackout(lines.data ?? [], rejects.data ?? [])
  } catch {
    return null
  }
}
